/**
 * ACCOUNT DELETION MODULE
 * Handles the account deletion flow with proper data export and share revocation
 *
 * Flow:
 * 1. Show deletion preview (documents, shares, etc.)
 * 2. Prompt to export data first
 * 3. Handle share revocation (for owned shares)
 * 4. Confirm deletion with password
 * 5. Call DELETE /api/account
 * 6. Clean up local state and redirect
 */

import { rotateShareDEK } from "../../../engine/sync/share-rotation.js";
import { exportAllData } from "../../../engine/crypto/data-export.js";
import { downloadExport, showExportWarnings } from "./export-flow.js";
import { chooseExportOptions } from './export-options.js';
import { passwordProof } from "../../../engine/auth/handlers.js";
import {
  clearAllCaches,
  deleteDocCache,
  clearPrefsByPrefix,
  removePref,
} from "../../../engine/storage/index.js";
import { t } from "../../../shared/i18n/index.js";
import { escapeHtml } from "../../../engine/core/validation.js";

// Dependencies injected at runtime
let _VaultStorage = null;
let _getMasterKey = null;
let _onLogout = null;

/**
 * Initialize the account deletion module
 * @param {Object} deps
 * @param {Object} deps.VaultStorage - VaultStorage instance
 * @param {Function} deps.getMasterKey - Function to get master key
 * @param {Function} deps.onLogout - Function to call after successful deletion
 */
export function initAccountDeletion(deps) {
  _VaultStorage = deps.VaultStorage;
  _getMasterKey = deps.getMasterKey;
  _onLogout = deps.onLogout;
}

/**
 * Show the account deletion modal
 * This is the main entry point for the deletion flow
 */
export async function showDeletionModal() {
  if (!_VaultStorage) {
    throw new Error("Account deletion not initialized");
  }

  // Fetch deletion preview from server
  let preview;
  try {
    const response = await fetch("/api/account/deletion-preview", {
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error("Failed to fetch deletion preview");
    }
    preview = await response.json();
  } catch (err) {
    console.error("[AccountDeletion] Failed to get preview:", err);
    alert(t("account.deletion.loadFailed"));
    return;
  }

  // Create modal
  const modal = createModal(preview);
  document.body.appendChild(modal);

  // Populate hidden username field for password managers
  const usernameInput = modal.querySelector("#deletion-username");
  if (usernameInput && preview.username) {
    usernameInput.value = preview.username;
  }

  // Setup event listeners
  setupModalEvents(modal, preview);
}

/**
 * Create the deletion modal HTML
 */
function createModal(preview) {
  const modal = document.createElement("div");
  modal.className = "deletion-modal-overlay";
  modal.id = "account-deletion-modal";

  // H-2: Backend sends nested will_delete object with personal_documents/shared_documents_owned
  const hasOwnedShares = (preview.will_delete?.shared_documents_owned ?? 0) > 0;
  const hasDocuments = (preview.will_delete?.personal_documents ?? 0) > 0;
  const documentCount = parseInt(preview.will_delete?.personal_documents, 10) || 0;
  const sharedCount = parseInt(preview.will_delete?.shared_documents_owned, 10) || 0;
  const text = (key, params) => escapeHtml(t(key, params));

  modal.innerHTML = `
    <div class="deletion-modal">
      <div class="deletion-modal-header">
        <h2>${text("html.profile.deleteAccount")}</h2>
        <button class="deletion-modal-close" title="${text("common.cancel")}">&times;</button>
      </div>

      <div class="deletion-modal-content">
        <!-- Warning -->
        <div class="deletion-warning">
          <span class="deletion-warning-icon">&#9888;</span>
          <div class="deletion-warning-text">
            <strong>${text("account.deletion.irreversible")}</strong>
            <p>${text("account.deletion.warning")}</p>
          </div>
        </div>

        <!-- Preview -->
        <div class="deletion-preview">
          <h3>${text("account.deletion.previewTitle")}</h3>
          <ul class="deletion-preview-list">
            <li>
              <span class="deletion-preview-icon">&#128196;</span>
              <span>${text("account.deletion.documents", { count: documentCount })}</span>
            </li>
            ${hasOwnedShares ? `
            <li>
              <span class="deletion-preview-icon">&#128279;</span>
              <span>${text("account.deletion.sharedOwned", { count: sharedCount })}</span>
              <span class="deletion-preview-note">${text("account.deletion.snapshotNote")}</span>
            </li>
            ` : ""}
            <li>
              <span class="deletion-preview-icon">&#128274;</span>
              <span>${text("account.deletion.keys")}</span>
            </li>
          </ul>
        </div>

        <!-- Export prompt -->
        <div class="deletion-export">
          <h3>${text("account.deletion.exportTitle")}</h3>
          <p>${text("account.deletion.exportAdvice")}</p>
          <button class="deletion-btn deletion-btn-secondary" id="deletion-export-btn">
            <span>&#128190;</span> ${text("account.deletion.exportAll")}
          </button>
          <div id="deletion-export-status" class="deletion-export-status"></div>
        </div>

        <!-- Confirmation -->
        <div class="deletion-confirm">
          <h3>${text("account.deletion.confirmTitle")}</h3>
          <p>${text("account.deletion.confirmPrompt")}</p>
          <form id="deletion-form" autocomplete="on">
            <input type="text" id="deletion-username" class="deletion-input-hidden"
                   autocomplete="username" aria-hidden="true" tabindex="-1" />
            <input type="password" id="deletion-password" class="deletion-input"
                   placeholder="${text("account.password.placeholder")}" autocomplete="current-password" />
          </form>
          <div id="deletion-error" class="deletion-error"></div>
        </div>
      </div>

      <div class="deletion-modal-footer">
        <button class="deletion-btn deletion-btn-cancel" id="deletion-cancel-btn">
          ${text("common.cancel")}
        </button>
        <button class="deletion-btn deletion-btn-danger" id="deletion-confirm-btn">
          <span>&#128465;</span> ${text("account.deletion.submit")}
        </button>
      </div>
    </div>
  `;

  return modal;
}

/**
 * Setup event listeners for the modal
 */
function setupModalEvents(modal, preview) {
  // Get all focusable elements in the modal
  const focusableElements = modal.querySelectorAll(
    'button, input, [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  // Close button
  modal.querySelector(".deletion-modal-close").addEventListener("click", () => {
    closeModal(modal);
  });

  // Cancel button
  modal.querySelector("#deletion-cancel-btn").addEventListener("click", () => {
    closeModal(modal);
  });

  // Click outside to close
  modal.addEventListener("click", (e) => {
    if (e.target === modal) {
      closeModal(modal);
    }
  });

  // Escape key to close and focus trapping
  const handleKeydown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closeModal(modal);
      return;
    }

    // Focus trapping - keep Tab within modal
    if (e.key === "Tab") {
      if (e.shiftKey) {
        // Shift+Tab: if on first element, go to last
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab: if on last element, go to first
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    }
  };
  document.addEventListener("keydown", handleKeydown);

  // Store handler reference for cleanup
  modal._keydownHandler = handleKeydown;

  // Export button
  modal.querySelector("#deletion-export-btn").addEventListener("click", async () => {
    await handleExport(modal);
  });

  // Confirm button
  modal.querySelector("#deletion-confirm-btn").addEventListener("click", async () => {
    await handleDeletion(modal, preview);
  });

  // Form submit - prevent default and trigger deletion
  modal.querySelector("#deletion-form").addEventListener("submit", (e) => {
    e.preventDefault();
    handleDeletion(modal, preview);
  });

  // Set initial focus on the close button (first focusable element)
  requestAnimationFrame(() => {
    firstFocusable?.focus();
  });
}

/**
 * Handle export button click
 */
async function handleExport(modal) {
  const options = await chooseExportOptions();
  if (!options) return;
  const exportBtn = modal.querySelector("#deletion-export-btn");
  const statusEl = modal.querySelector("#deletion-export-status");

  exportBtn.disabled = true;
  exportBtn.innerHTML = `<span>&#9203;</span> ${escapeHtml(t("account.deletion.exporting"))}`;
  statusEl.textContent = "";

  try {
    const result = await exportAllData({
      ...options,
      onProgress: (current, total, name) => {
        statusEl.textContent = t("account.deletion.exportProgress", { current, total, name });
      },
    });
    const { exportData, exportErrors } = result;

    const saved = await downloadExport(exportData, null, options);
    const warnings = saved?.warnings || exportErrors;

    if (!options.includeAttachments && exportErrors && exportErrors.length > 0) {
      showExportWarnings(exportErrors);
    }
    statusEl.textContent = warnings?.length
      ? t("account.deletion.backupWarnings")
      : options.includeAttachments ? t("account.deletion.backupDone")
        : t("account.deletion.backupMapOnly");
    statusEl.className = `deletion-export-status ${warnings?.length ? "error" : "success"}`;

    exportBtn.innerHTML = `<span>&#9989;</span> ${escapeHtml(t("account.deletion.exported"))}`;
    setTimeout(() => {
      exportBtn.disabled = false;
      exportBtn.innerHTML = `<span>&#128190;</span> ${escapeHtml(t("account.deletion.exportAll"))}`;
    }, 3000);
  } catch (err) {
    console.error("[AccountDeletion] Export failed:", err);
    statusEl.textContent = t("account.deletion.exportFailed");
    statusEl.className = "deletion-export-status error";
    exportBtn.disabled = false;
    exportBtn.innerHTML = `<span>&#128190;</span> ${escapeHtml(t("account.deletion.exportAll"))}`;
  }
}

/**
 * Handle deletion confirmation
 */
let _deletionInProgress = false;

async function handleDeletion(modal, preview) {
  // L-8: Prevent double-submit
  if (_deletionInProgress) return;

  const passwordInput = modal.querySelector("#deletion-password");
  const confirmBtn = modal.querySelector("#deletion-confirm-btn");
  const errorEl = modal.querySelector("#deletion-error");

  const password = passwordInput.value;
  if (!password) {
    errorEl.textContent = t("account.deletion.passwordRequired");
    passwordInput.focus();
    return;
  }

  _deletionInProgress = true;
  confirmBtn.disabled = true;
  confirmBtn.innerHTML = `<span>&#9203;</span> ${escapeHtml(t("account.deletion.processing"))}`;
  errorEl.textContent = "";

  try {
    // Step 0: Check the password (locally, then proven to the server without
    // sending it) BEFORE revoking anything: revocation cannot be undone.
    confirmBtn.innerHTML = `<span>&#9203;</span> ${escapeHtml(t("account.deletion.checkingPassword"))}`;
    const proof = await passwordProof(password);

    // Step 1: Revoke all owned shares (create snapshots for recipients)
    if ((preview.will_delete?.shared_documents_owned ?? 0) > 0) {
      confirmBtn.innerHTML = `<span>&#9203;</span> ${escapeHtml(t("account.deletion.revoking"))}`;
      await revokeAllOwnedShares();
    }

    // Step 2: Call deletion API
    confirmBtn.innerHTML = `<span>&#9203;</span> ${escapeHtml(t("account.deletion.deleting"))}`;
    const response = await fetch("/api/account", {
      method: "DELETE",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      credentials: "include",
      body: JSON.stringify(proof),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || t("account.deletion.failed", { status: String(response.status) }));
    }

    // Step 3: Clean up local data
    confirmBtn.innerHTML = `<span>&#9203;</span> ${escapeHtml(t("account.deletion.cleaning"))}`;
    await cleanupLocalData();

    // Step 4: Show success and redirect
    closeModal(modal);
    _deletionInProgress = false;
    alert(t("account.deletion.done"));

    // Call logout handler
    if (_onLogout) {
      _onLogout();
    } else {
      // Fallback: redirect to login
      window.location.href = "/";
    }
  } catch (err) {
    console.error("[AccountDeletion] Deletion failed:", err);
    errorEl.textContent = err.message;
    _deletionInProgress = false;
    confirmBtn.disabled = false;
    confirmBtn.innerHTML = `<span>&#128465;</span> ${escapeHtml(t("account.deletion.submit"))}`;
  }
}

/**
 * Revoke all shares owned by the user via the MODERN rotate+revoke path.
 *
 * Previously this used the legacy POST /shares/:id/revoke flow with a
 * client-built `createRevocationSnapshot()` payload. That helper only worked
 * when `getSharedDocManager(shareId)` returned a manager — i.e. the doc had
 * been opened as a shared-doc view in this session. Owned shared docs are
 * commonly open as the MAIN doc (via handleDocOpen) or not open at all,
 * so the snapshot returned null and the loop aborted, blocking deletion.
 *
 * The modern `rotateShareDEK` flow:
 *  - loads doc state from memory → IndexedDB → server (decrypts with old DEK)
 *  - creates the revocation snapshot atomically inside /rotate
 *  - rewraps the new DEK only for non-revoked members (none, in this case)
 *  - force-closes any open WS sessions for the revoked users
 *  - includes the full current state (baseline + un-compacted deltas) in
 *    the snapshot, since the snapshot now uses the client-supplied
 *    pre_rotation_snapshot (see Fix #1) rather than the stale server baseline
 */
async function revokeAllOwnedShares() {
  // Fetch owned shares
  const response = await fetch("/api/shares", {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(t("account.deletion.sharesFetchFailed"));
  }

  const shares = await response.json();
  const ownedShares = shares.filter((s) => s.role === "owner");

  if (ownedShares.length === 0) {
    return;
  }

  console.log(`[AccountDeletion] Revoking ${ownedShares.length} owned shares via rotate+revoke`);

  const failedRevocations = [];

  for (const share of ownedShares) {
    try {
      // Find non-owner members of this share. If none, nothing to revoke.
      const permsResp = await fetch(`/api/shares/${share.id}/permissions`, {
        credentials: "include",
      });
      if (!permsResp.ok) {
        throw new Error(`Failed to fetch permissions: ${permsResp.status}`);
      }
      const permissions = await permsResp.json();
      const nonOwnerIds = permissions
        .filter((p) => p.role !== "owner")
        .map((p) => p.user_id);

      if (nonOwnerIds.length === 0) {
        // No recipients — nothing to revoke. The doc will be cleaned up by
        // the account DELETE cascade like any other private owned doc.
        console.log(`[AccountDeletion] Share ${share.id} has no recipients, skipping rotation`);
        continue;
      }

      // Atomic rotate+revoke: snapshot is captured server-side from the
      // client-supplied pre_rotation_snapshot (full state under OLD DEK),
      // recipients are revoked, WS sessions force-closed.
      await rotateShareDEK(share.id, { revokeUserIds: nonOwnerIds });
      console.log(`[AccountDeletion] Revoked ${nonOwnerIds.length} recipient(s) on share: ${share.id}`);
    } catch (err) {
      failedRevocations.push(share.id);
      console.warn(`[AccountDeletion] Failed to revoke share ${share.id}:`, err);
    }
  }

  // M-3: Abort if any revocations failed — recipients would lose data without recovery snapshots
  if (failedRevocations.length > 0) {
    throw new Error(t("account.deletion.revokeFailed", { count: failedRevocations.length }));
  }
}

/**
 * Clean up all local data after account deletion
 */
async function cleanupLocalData() {
  console.log("[AccountDeletion] Cleaning up local data...");

  // Clear VaultStorage cache
  if (_VaultStorage) {
    _VaultStorage.clear();
  }

  // Clear all Orbital local caches (IDB + matching prefs) via the adapter.
  try {
    const { docs, prefs } = await clearAllCaches();
    console.log(`[AccountDeletion] clearAllCaches: ${docs} caches, ${prefs} prefs`);
  } catch (err) {
    console.warn("[AccountDeletion] clearAllCaches failed:", err);
  }

  // Fallback for browsers without indexedDB.databases() (Firefox): delete
  // the known doc caches one by one using ids from the vault index.
  const index = _VaultStorage?.getIndex();
  if (index?.docs) {
    for (const map of index.docs) {
      try {
        await deleteDocCache(map.docId);
      } catch {}
    }
  }

  // Additional cleanup the adapter does not cover by default:
  //  - "vault-*" prefs (legacy keys outside the adapter's known prefixes)
  //  - mobile stash keys with plaintext note/node edits (see interfaces/classic/mobile/index.js)
  // These are kept inline so they remain explicit at the deletion site.
  clearPrefsByPrefix("vault-");
  removePref("mobile-unsaved-note-edits");
  removePref("mobile-unsaved-node-edits");

  // Clear sessionStorage (per-tab session masking, not a persistence concern)
  sessionStorage.clear();

  console.log("[AccountDeletion] Local data cleanup complete");
}

/**
 * Close the modal
 */
function closeModal(modal) {
  // Don't close while deletion is in progress — the request may still be
  // running server-side and closing would hide the result from the user
  if (_deletionInProgress) return;

  // Clean up keydown handler
  if (modal._keydownHandler) {
    document.removeEventListener("keydown", modal._keydownHandler);
  }
  modal.remove();
}

/**
 * Export for use in other modules
 */
export { cleanupLocalData };
